import React, { useCallback } from 'react';

import { TDayTripEvent } from 'components/Pages/Admin/Itinerary/Details/DetailsChronologicalMode/types';
import { Wysiwyg } from 'components/shared/Wysiwyg';
import { LOREM_IPSUM_TEXT } from 'constants/texts';
import { IRichText } from 'detailsv2/__blocks';
import {
  ETimelineType,
  ETripEvent,
  IAccommodationEventData,
  IActivityEventData,
  ITransferEventData,
  TFlightEventData,
} from 'detailsv2/structure';
import i18n from 'i18n';

import s from './index.module.css';

const addDetailRow = (key: string, value: string | undefined, res: JSX.Element[]) => {
  if (value) {
    res.push(
      <div>
        {i18n.t(`proposal_content.event_details.${key}`)}: {value}
      </div>,
    );
  }
};

export const useTravelBriefMoreDetails = ({ tripEvent }: { tripEvent: TDayTripEvent }) => {
  const detailsConfig = useCallback(
    (type: string) => {
      const res: JSX.Element[] = [];

      const getLink = (link: string) => {
        return link.startsWith('http') ? link : `https://${link}`;
      };

      const getNotes = (notes: IRichText) => {
        return <Wysiwyg data={notes} placeholder={LOREM_IPSUM_TEXT} isReadOnly />;
      };

      switch (type) {
        case ETripEvent.Flight: {
          const flight = tripEvent.data as TFlightEventData;
          if (!flight.fromFsService) {
            if (flight.bookedBy)
              res.push(
                <div>
                  {i18n.t('proposal_content.event_details.booked_by')}: {flight.bookedBy}
                </div>,
              );
            if (flight.confirmationNumber)
              res.push(
                <div>
                  {i18n.t('proposal_content.event_details.confirmation_number')}: {flight.confirmationNumber}
                </div>,
              );
            if (flight.duration)
              res.push(
                <div>
                  {' '}
                  {i18n.t('proposal_content.event_details.flights.duration')}: {flight.duration}
                </div>,
              );
          }

          if (flight.notes?.text) {
            if (res.length > 0) res.push(<br />);

            res.push(<div className={s.tableText}>{getNotes(flight.notes)}</div>);
          }

          return res;
        }
        case ETripEvent.Transfer: {
          const transfer = tripEvent.data as ITransferEventData;

          /* common data */
          addDetailRow('confirmation_number', transfer.confirmationNumber, res);
          addDetailRow('duration', transfer.duration, res);

          /* transfer & other data */
          addDetailRow('booked_by', transfer.bookedBy, res);
          addDetailRow('provider', transfer.provider, res);
          addDetailRow('contact_info', transfer.contactInfo, res);
          addDetailRow('vehicle_type', transfer.vehicleType, res);

          /* rentalCar data */
          addDetailRow('vehicle_number', transfer.vehicleNumber, res);
          addDetailRow('rental_company_name', transfer.rentalCompanyName, res);
          addDetailRow('rental_company_address', transfer.rentalCompanyAddress, res);

          /* train data */
          addDetailRow('train_type', transfer.trainType, res);
          addDetailRow('train_number', transfer.trainNumber, res);
          addDetailRow('coach_number', transfer.coachNumber, res);
          addDetailRow('seats', transfer.seats, res);
          addDetailRow('service_class', transfer.serviceClass, res);

          if (transfer.notes?.text) {
            if (res.length > 0) res.push(<br />);

            res.push(<div className={s.tableText}>{getNotes(transfer.notes)}</div>);
          }

          return res;
        }
        case ETripEvent.Accommodation: {
          const accom = tripEvent.data as IAccommodationEventData;

          if (tripEvent.timelineType && [ETimelineType.Middle, ETimelineType.End].includes(tripEvent.timelineType))
            return res;

          if (accom.rooming.length !== 0)
            res.push(
              <div>
                {i18n.t('proposal_content.event_details.room_type')}:{' '}
                {accom.rooming.map((room) => `${room.name} ${room.count && room.count > 0 ? ` x ${room.count} ` : ''}`)}
              </div>,
            );
          if (accom.roomBasis)
            res.push(
              <div>
                {' '}
                {i18n.t('proposal_content.event_details.basis_of_stay')}: {accom.roomBasis}
              </div>,
            );
          if (accom.confirmationNumber)
            res.push(
              <div>
                {i18n.t('proposal_content.event_details.confirmation_number')}: {accom.confirmationNumber}
              </div>,
            );
          if (accom.contactInfo)
            res.push(
              <div>
                {i18n.t('proposal_content.event_details.contact_info')}: {accom.contactInfo}
              </div>,
            );
          if (accom.website) res.push(<Website text={accom.website} url={getLink(accom.website)} />);
          if (accom.location)
            res.push(
              <div>
                {i18n.t('proposal_content.event_details.location')}: {accom.location}
              </div>,
            );

          if (accom.notes?.text) {
            if (res.length > 0) res.push(<br />);

            res.push(<div className={s.tableText}>{getNotes(accom.notes)}</div>);
          }
          return res;
        }
        case ETripEvent.Activity: {
          const act = tripEvent.data as IActivityEventData;

          if (tripEvent.timelineType && [ETimelineType.Middle, ETimelineType.End].includes(tripEvent.timelineType))
            return res;

          if (act.confirmationNumber)
            res.push(
              <div>
                {i18n.t('proposal_content.event_details.confirmation_number')}: {act.confirmationNumber}
              </div>,
            );
          if (act.contactInfo)
            res.push(
              <div>
                {i18n.t('proposal_content.event_details.contact_info')}: {act.contactInfo}
              </div>,
            );
          if (act.website) res.push(<Website text={act.website} url={getLink(act.website)} />);
          if (act.location)
            res.push(
              <div>
                {i18n.t('proposal_content.event_details.location')}: {act.location}
              </div>,
            );

          if (act.notes?.text) {
            if (res.length > 0) res.push(<br />);

            res.push(<div className={s.tableText}>{getNotes(act.notes)}</div>);
          }
          return res;
        }
        default:
          return res;
      }
    },
    [tripEvent.data, tripEvent.timelineType],
  );

  return { detailsConfig };
};

const Website = ({ text, url }: { text: string; url: string }) => {
  return (
    <div className={s.website}>
      {i18n.t('proposal_content.event_details.website')}:{' '}
      <a
        href={url}
        onClick={(event) => event.stopPropagation()}
        className={s.realLink}
        target="_blank"
        rel="noopener noreferrer"
        data-external-link=""
      >
        {text}
      </a>
    </div>
  );
};
